import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth-store';
import { authApi } from '../lib/api-client';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [showTwoFA, setShowTwoFA] = useState(false);
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setTokens, setUser } = useAuthStore();

  const loginMutation = useMutation({
    mutationFn: () => authApi.login(email, password),
    onSuccess: (data) => {
      setTempToken(data.tempToken);
      setShowTwoFA(true);
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Login failed');
    },
  });

  const verify2FAMutation = useMutation({
    mutationFn: () => authApi.verify2FA(twoFACode, tempToken),
    onSuccess: async (data) => {
      queryClient.clear();
      setTokens(data.accessToken, data.refreshToken);

      try {
        const profile = await authApi.getProfile();
        setUser(profile);
        navigate('/dashboard');
      } catch {
        setError('Failed to load profile');
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || '2FA verification failed');
    },
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    loginMutation.mutate();
  };

  const handleVerify2FA = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    verify2FAMutation.mutate();
  };

  return (
    <>
      <div className="auth">
        {/* Art Side - Left */}
        <aside className="authart">
          <a href="/" className="block z-[2] relative">
            <img src="/assets/ocn-w-logo.png" alt="OCN" width={131} height={48} />
          </a>

          {/* SVG Art Scene */}
          <div className="absolute inset-[100px_-15px_240px] flex items-center justify-center pointer-events-none">
            <img
              src="/assets/scenes/401.svg"
              alt=""
              className="w-[90%] h-full max-w-[440px] max-h-[400px]"
            />
          </div>

          <div className="artwords">
            <span className="eyebrow">Opaskwayak Cree Nation</span>
            <h1>
              A workspace for
              <br />
              your community.
            </h1>
            <p>A thoughtful workspace for the people who keep our community connected.</p>
          </div>

          <div className="artfooter">
            <span>Connected through OCN.</span>
            <span>OCN / Administration</span>
          </div>
        </aside>

        {/* Form Side - Right */}
        <main className="authbody">
          {/* Back link - Desktop only */}
          <a href="/" className="authback">
            <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.65}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            All portals
          </a>

          {/* Mobile logo */}
          <div className="authsmallbrand">
            <img src="/assets/ocn-w-logo.png" alt="OCN" width={110} height={40} />
          </div>

          <div className="authinner animate-rise">
            {!showTwoFA ? (
              <>
                <span className="eyebrow">OCN / Administration</span>
                <h1>Sign in</h1>
                <p>Access your administration workspace.</p>

                <form onSubmit={handleLogin} className="space-y-[21px]">
                  <div className="field">
                    <label htmlFor="email">Email address</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="admin@ocn.ca"
                      autoComplete="email"
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="password">Password</label>
                    <div className="passwordfield relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        className="pr-[48px]"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-[3px] top-[3px] border-0 bg-transparent h-[38px] w-[40px] text-[#7c8670] grid place-items-center"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        <svg
                          className="icon w-[16px] h-[16px]"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          {showPassword ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.65}
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.65}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          )}
                        </svg>
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="errorbox" role="alert">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loginMutation.isPending}
                    className="btn primary w-full"
                  >
                    {loginMutation.isPending ? (
                      'Signing in...'
                    ) : (
                      <>
                        Sign in
                        <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.65}
                            d="M5 12h14m-6-6l6 6-6 6"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </form>

                <div className="authlinks">
                  Need access?{' '}
                  <button
                    className="link"
                    onClick={() => alert('Contact your administrator for access')}
                  >
                    Contact your administrator
                  </button>
                </div>

                <div className="security">
                  <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.65}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  Secure access
                </div>

                <p className="authfoot">
                  Use your registered email address to access OCN services.
                  <br />
                  Verification codes are sent securely to your email.
                </p>
              </>
            ) : (
              <>
                <span className="eyebrow">OCN / Administration</span>
                <h1>Check your email.</h1>
                <p>Enter the 6-digit verification code sent to your email.</p>

                <form onSubmit={handleVerify2FA} className="space-y-[21px]">
                  <div className="field">
                    <label htmlFor="otp">Verification code</label>
                    <input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      maxLength={6}
                      className="otp"
                      placeholder="000000"
                      autoComplete="one-time-code"
                    />
                  </div>

                  {error && (
                    <div className="errorbox" role="alert">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={verify2FAMutation.isPending || twoFACode.length !== 6}
                    className="btn primary w-full"
                  >
                    {verify2FAMutation.isPending ? (
                      'Verifying...'
                    ) : (
                      <>
                        Verify and continue
                        <svg className="icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.65}
                            d="M5 12h14m-6-6l6 6-6 6"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </form>

                <div className="authlinks">
                  Didn't receive a code?{' '}
                  <button
                    onClick={() => loginMutation.mutate()}
                    className="link"
                    disabled={loginMutation.isPending}
                  >
                    Resend code
                  </button>
                  <br />
                  <button
                    onClick={() => {
                      setShowTwoFA(false);
                      setTwoFACode('');
                      setError('');
                    }}
                    className="link mt-2 inline-block"
                  >
                    Back to sign in
                  </button>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <style>{`
        .auth {
          display: grid;
          grid-template-columns: 43% 57%;
          min-height: 100svh;
          background: #faf9f6;
        }

        @media (max-width: 1023px) {
          .auth {
            display: block;
          }
        }

        .authart {
          background: #e8d8c9;
          margin: 16px 0 16px 16px;
          border-radius: 21px;
          padding: 34px 38px;
          min-height: 0;
          height: calc(100vh - 32px);
          position: sticky;
          top: 16px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        @media (max-width: 1023px) {
          .authart {
            display: none;
          }
        }

        .artwords {
          margin-top: auto;
          max-width: 370px;
          padding: 30px 0 0;
          position: relative;
          z-index: 2;
        }

        .eyebrow {
          font-size: 10px;
          color: #a4896f;
          letter-spacing: 1.4px;
          text-transform: uppercase;
          font-weight: 600;
        }

        .artwords h1 {
          font-size: 41px;
          line-height: 1.08;
          font-weight: 600;
          letter-spacing: -1.5px;
          color: #5b5249;
          margin-top: 13px;
        }

        .artwords p {
          font-size: 13px;
          line-height: 1.7;
          color: #a48e77;
          max-width: 300px;
          margin-top: 15px;
        }

        .artfooter {
          font-size: 10px;
          margin-top: 28px;
          color: #a48c75;
          display: flex;
          justify-content: space-between;
          position: relative;
          z-index: 2;
        }

        .authbody {
          padding: 65px 55px;
          background: #faf9f6;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .authback {
          position: absolute;
          top: 29px;
          right: 35px;
          font-size: 10px;
          color: #68716d;
          align-items: center;
          gap: 8px;
          z-index: 10;
          display: none;
        }

        @media (min-width: 1024px) {
          .authback {
            display: flex;
          }
        }

        .authback .icon {
          width: 13px;
          height: 13px;
        }

        .authsmallbrand {
          display: block;
          margin-bottom: 30px;
        }

        @media (min-width: 1024px) {
          .authsmallbrand {
            display: none;
          }
        }

        .authinner {
          width: 100%;
          max-width: 385px;
        }

        .authinner .eyebrow {
          font-size: 10px;
          color: #68716d;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          font-weight: 600;
          display: block;
          margin-bottom: 13px;
        }

        .authinner h1 {
          font-size: 32px;
          font-weight: 600;
          color: #424a47;
          margin: 13px 0 9px;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .authinner > p {
          font-size: 14px;
          color: #68716d;
          line-height: 1.6;
          margin-bottom: 28px;
        }

        .field {
          display: flex;
          flex-direction: column;
          gap: 7px;
          margin-bottom: 21px;
        }

        .field label {
          font-weight: 500;
          font-size: 13px;
          color: #424a47;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 1px solid #e6e9de;
          background: white;
          color: #424a47;
          padding: 10px 14px;
          border-radius: 8px;
          min-height: 44px;
          font-weight: 500;
          font-size: 12px;
          transition: background 0.18s, transform 0.18s;
          cursor: pointer;
        }

        .btn:hover {
          background: #f0f1e9;
        }

        .btn:active {
          transform: scale(0.98);
        }

        .btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .btn.primary {
          background: #242728;
          color: #fff;
          border-color: #242728;
        }

        .btn.primary:hover {
          background: #3c413d;
        }

        .btn .icon {
          width: 15px;
          height: 15px;
          stroke-width: 1.65;
        }

        .link {
          color: #68716d;
          font-weight: 500;
          font-size: 11px;
          background: none;
          border: 0;
          padding: 0;
          cursor: pointer;
        }

        .link:hover {
          text-decoration: underline;
        }

        .authlinks .link {
          color: #424a47;
        }

        .authlinks {
          font-size: 12px;
          color: #68716d;
          text-align: center;
          margin-top: 21px;
        }

        .security {
          display: flex;
          justify-content: center;
          gap: 6px;
          align-items: center;
          font-size: 11px;
          color: #68716d;
          margin-top: 25px;
        }

        .security .icon {
          width: 13px;
          height: 13px;
        }

        .authfoot {
          font-size: 11px;
          line-height: 1.7;
          color: #68716d;
          margin-top: 30px;
        }

        .errorbox {
          font-size: 12px;
          background: #fff1e6;
          border: 1px solid #f0d8c1;
          color: #a16932;
          border-radius: 8px;
          padding: 14px;
          margin-bottom: 18px;
        }

        .otp {
          font-size: 27px;
          letter-spacing: 14px;
          text-align: center;
          font-variant-numeric: tabular-nums;
          color: #424a47;
        }

        .icon {
          width: 18px;
          height: 18px;
          fill: none;
          stroke: currentColor;
          stroke-width: 1.65;
          stroke-linecap: round;
          stroke-linejoin: round;
          flex-shrink: 0;
        }

        @media (max-width: 1023px) {
          .authbody {
            min-height: 100svh;
            padding: 90px 27px 35px;
          }

          .authback {
            top: 26px;
            right: 26px;
          }

          .authinner {
            max-width: 400px;
          }

          .authinner h1 {
            font-size: 31px;
          }
        }
      `}</style>
    </>
  );
}
